import { NavButton } from "@/components/nav-button";
import { isCompletionCtaTarget } from "@/lib/checkout/completion-cta-target";
import type { MouseEventHandler } from "react";

type CompletionCtaContent = {
  ctaUrl?: string | null;
  ctaText?: string | null;
  ctaTarget?: string | null;
  secondaryCtaUrl?: string | null;
  secondaryCtaText?: string | null;
  secondaryCtaTarget?: string | null;
};

type CompletionStatusButtonsProps = {
  content: CompletionCtaContent;
  /** When `ctaUrl` is set, use this instead of the default `NavButton`. */
  primaryCta?: React.ReactElement;
  primaryFallback?: React.ReactNode;
  secondaryFallback?: React.ReactNode;
  secondaryOnLinkClick?: MouseEventHandler<HTMLAnchorElement>;
  /**
   * Failure CMS docs (FAILED / membership fulfilment) may still use
   * secondaryCtaText + secondaryCtaUrl without a target. Success screens must
   * keep requiring an explicit destination so leftover fields stay hidden.
   */
  allowTargetlessSecondary?: boolean;
};

export function CompletionStatusButtons({
  content,
  primaryCta,
  primaryFallback,
  secondaryFallback,
  secondaryOnLinkClick,
  allowTargetlessSecondary = false,
}: CompletionStatusButtonsProps) {
  const primary = content.ctaUrl
    ? (primaryCta ?? (
        <NavButton href={content.ctaUrl}>{content.ctaText}</NavButton>
      ))
    : primaryFallback;

  const secondaryDestination = isCompletionCtaTarget(content.secondaryCtaTarget)
    ? content.secondaryCtaTarget
    : // Explicit None ("") must omit the button; only legacy missing targets
      // (null/undefined) may fall back to a custom URL on failure screens.
      allowTargetlessSecondary &&
        content.secondaryCtaTarget == null &&
        content.secondaryCtaUrl &&
        content.secondaryCtaText
      ? "customUrl"
      : null;

  const secondary =
    secondaryDestination && content.secondaryCtaText ? (
      content.secondaryCtaUrl ? (
        <NavButton
          href={content.secondaryCtaUrl}
          variant="secondary"
          onLinkClick={secondaryOnLinkClick}
        >
          {content.secondaryCtaText}
        </NavButton>
      ) : secondaryDestination === "backToPayment" ? (
        secondaryFallback
      ) : null
    ) : null;

  if (!primary && !secondary) {
    return undefined;
  }

  return (
    <div className="flex w-full flex-col gap-3">
      {primary}
      {secondary}
    </div>
  );
}
